package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.PeriodMode
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.domain.model.AppNotification
import br.com.vvrefeicoes.portal.domain.model.NotificationPriority
import br.com.vvrefeicoes.portal.domain.model.NotificationsFeed
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.NotificationRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteNotificationRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : NotificationRepository {
    override suspend fun loadFeed(
        page: Int,
        size: Int,
        query: String,
        selection: PeriodSelection,
    ): NotificationsFeed {
        val url =
            buildString {
                append(baseUrl)
                    .append("/notifications?page=")
                    .append(page)
                    .append("&size=")
                    .append(size)
                when (selection.mode) {
                    PeriodMode.MONTH -> append("&period=mensal&year=${selection.month.year}&month=${selection.month.month}")
                    PeriodMode.YEAR -> append("&period=anual&year=${selection.year}")
                    PeriodMode.TOTAL -> Unit
                    PeriodMode.CUSTOM -> append("&period=personalizado&start=${selection.start.id}&end=${selection.end.id}")
                }
                if (query.isNotBlank()) append("&q=").append(query.encodeURLParameter())
            }
        val response = httpClient.get(url) { authorize() }
        ensureSuccess(response.status.value, response.status.isSuccess(), response.bodyAsText())
        return response.body<NotificationsFeedDto>().toDomain()
    }

    override suspend fun markRead(id: String) {
        val response = httpClient.post(baseUrl + "/notifications/$id/read") { authorize() }
        ensureSuccess(response.status.value, response.status.isSuccess(), response.bodyAsText())
    }

    override suspend fun dismiss(id: String) {
        val response = httpClient.post(baseUrl + "/notifications/$id/dismiss") { authorize() }
        ensureSuccess(response.status.value, response.status.isSuccess(), response.bodyAsText())
    }

    private fun ensureSuccess(
        status: Int,
        success: Boolean,
        body: String,
    ) {
        if (success) return
        val message =
            runCatching {
                Json { ignoreUnknownKeys = true }
                    .decodeFromString<PanelApiErrorDto>(body)
                    .message
            }.getOrNull()
        error(message ?: "HTTP $status")
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
internal data class NotificationItemDto(
    val id: String = "",
    val title: String = "",
    val body: String = "",
    val priority: String = "LOW",
    val imageUrl: String? = null,
    val publishedAt: String? = null,
    val read: Boolean = false,
    val dismissed: Boolean = false,
)

@Serializable
private data class NotificationsFeedDto(
    val items: List<NotificationItemDto> = emptyList(),
    val unreadCount: Long = 0,
    val totalPages: Int = 1,
)

private fun NotificationsFeedDto.toDomain(): NotificationsFeed =
    NotificationsFeed(
        items = items.map { it.toDomain() },
        unreadCount = unreadCount.toInt(),
        totalPages = totalPages,
    )

internal fun NotificationItemDto.toDomain(): AppNotification =
    AppNotification(
        id = id,
        title = title,
        body = body,
        priority = priority.toPriority(),
        imageUrl = imageUrl,
        publishedAt = publishedAt,
        read = read,
        dismissed = dismissed,
    )

internal fun String.toPriority(): NotificationPriority = NotificationPriority.entries.firstOrNull { it.name == this } ?: NotificationPriority.LOW

/** Decodifica o payload de notificação enviado pelo WebSocket (mesmo formato do item do feed). */
internal fun decodeNotificationItem(body: String): AppNotification? =
    runCatching {
        Json { ignoreUnknownKeys = true }
            .decodeFromString<NotificationItemDto>(body)
            .toDomain()
    }.getOrNull()
